import React from 'react';
import { Check, Loader2, Plus, Trash2, X } from 'lucide-react';
import { TextField, ImageDrop, inputCls, btnGhost } from '../ui.jsx';
import { ICON_LABELS } from '../../../../components/producerPage/icons.js';
import { EMPTY_STAT, MAX_STATS } from '../../../../lib/producerPage.js';

const cleanSlug = (v) => v.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40);

function SlugStatus({ slug, value }) {
  if (!value) return null;
  if (slug.checking) return <Loader2 className="h-4 w-4 animate-spin text-white/40" aria-label="Checking" />;
  if (slug.available) return <Check className="h-4 w-4 text-emerald-400" aria-label="Available" />;
  return <X className="h-4 w-4 text-rose-400" aria-label="Unavailable" />;
}

/** Step 1 — page title, link, header, background picture and business stats. */
export default function TitleStep({ ctx }) {
  const { page, patch, slug, server, assets, upload, remove, busy } = ctx;
  const setStat = (i, k, v) => patch({ stats: page.stats.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });
  const suggested = server?.suggestedSlug;
  const slugError = page.slug && !slug.checking && slug.available === false ? slug.error || 'That page link is not available.' : '';

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold">Title</h3>
        <p className="text-xs text-white/60">This section configures how your leads will navigate to your producer page. Keep the page link and title short for better search results.</p>
      </div>

      <TextField label="Page title" value={page.title} onChange={(v) => patch({ title: v })} maxLength={80} required testId="pp-title" />

      <div>
        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-white/50">Page link *</span>
        <div className={`flex items-center gap-2 rounded-lg border bg-white/5 px-3 ${slugError ? 'border-rose-500/60' : 'border-white/10'}`}>
          <span className="shrink-0 rounded bg-white/10 px-2 py-1 text-xs text-white/60">civixcrm.ai/p/</span>
          <input
            className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-white focus:outline-hidden"
            value={page.slug}
            onChange={(e) => patch({ slug: cleanSlug(e.target.value) })}
            placeholder={suggested || 'your-name'}
            aria-label="Page link"
            data-testid="pp-slug"
          />
          <SlugStatus slug={slug} value={page.slug} />
        </div>
        {slugError && <p className="mt-1 text-xs text-rose-400" role="alert">{slugError}</p>}
        {!page.slug && suggested && (
          <button type="button" className="mt-1 text-xs text-civix-purpleSoft underline" onClick={() => patch({ slug: cleanSlug(suggested) })}>
            Use “{suggested}”
          </button>
        )}
      </div>

      <TextField label="Header title" value={page.headerTitle} onChange={(v) => patch({ headerTitle: v })} maxLength={160} required testId="pp-header" />

      <div>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-white/50">Background picture (optional)</div>
        <ImageDrop label={assets.background ? 'Replace background picture (max 5MB)' : 'Upload background picture (max 5MB)'} onFile={(f) => upload('background', f)} busy={busy} testId="pp-bg-upload" />
        {assets.background && (
          <button type="button" className="mt-1 text-xs text-rose-300 underline" onClick={() => remove('background')}>Remove background picture</button>
        )}
      </div>

      <div>
        <div className="flex items-end justify-between gap-3">
          <div>
            <h4 className="text-base font-semibold">Business stats</h4>
            <p className="text-xs text-white/60">Add up to {MAX_STATS} stats about your business. Only add figures you can stand behind — leads will read these as claims.</p>
          </div>
          <button type="button" className={btnGhost} disabled={page.stats.length >= MAX_STATS} onClick={() => patch({ stats: [...page.stats, { ...EMPTY_STAT }] })} data-testid="pp-add-stat">
            <Plus className="mr-1 inline h-3.5 w-3.5" />Add business stat
          </button>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {page.stats.map((s, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-white/10 p-3" data-testid={`pp-stat-${i}`}>
              <div className="flex items-center gap-2">
                <select className={`${inputCls} flex-1`} value={s.icon} onChange={(e) => setStat(i, 'icon', e.target.value)} aria-label="Icon">
                  {Object.entries(ICON_LABELS).map(([k, l]) => <option key={k} value={k} className="text-black">{l}</option>)}
                </select>
                <button type="button" aria-label="Delete stat" className="rounded p-2 text-rose-400 hover:bg-rose-500/10" onClick={() => patch({ stats: page.stats.filter((_, j) => j !== i) })}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <TextField label="Title" value={s.title} onChange={(v) => setStat(i, 'title', v)} maxLength={30} required />
              <TextField label="Description" value={s.description} onChange={(v) => setStat(i, 'description', v)} maxLength={120} required />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
